import {Box, Link, Token} from '@primer/react'
import {graphql, useFragment} from 'react-relay'

import {issueHovercardPath} from '@github-ui/paths'
import type {HeaderParentTitle$key} from './__generated__/HeaderParentTitle.graphql'

import {getIcon} from '../sections/relations-section/ParentIssue'
import type {OptionConfig} from '../OptionConfig'

type HeaderParentTitleProps = {
  parentKey?: HeaderParentTitle$key
  small?: boolean
} & SharedProps

type SharedProps = {
  optionConfig?: OptionConfig
}

/** The internal component that renders the parent title itself after all data fetching has occurred */
export const HeaderParentTitle = ({parentKey, optionConfig, small = false}: HeaderParentTitleProps) => {
  const data = useFragment(
    graphql`
      fragment HeaderParentTitle on Issue {
        parent {
          number
          repository {
            name
            owner {
              login
            }
          }
          state
          stateReason
          title
          url
        }
      }
    `,
    parentKey,
  )

  if (!data) {
    return null
  }
  const {parent} = data

  // The data from this fragment is not _all_ available yet, as it's requested in the secondary query, however,
  // AddSubIssueButtonGroup requests parent.id which is available in the primary query. useFragment, seemingly
  // merges this partial data and we are left with a parent object that is not empty, but also not fully populated.
  // See: https://github.com/github/github/issues/333236
  if (!parent?.repository) {
    return null
  }

  const repo = parent.repository.name
  const owner = parent.repository.owner.login

  const itemIdentifier = {number: parent.number, repo, owner, type: 'Issue'} as const

  const stateIcon = getIcon(parent.state, parent.stateReason, {width: 14, height: 14, marginRight: 1})
  if (!stateIcon) return null

  const handleClick = (e: React.MouseEvent) => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return

    if (optionConfig?.onParentIssueActivate) {
      optionConfig.onParentIssueActivate(e, itemIdentifier)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
    if (e.key !== 'Enter' || e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return

    if (optionConfig?.onParentIssueActivate) {
      optionConfig.onParentIssueActivate(e, itemIdentifier)
    }
  }

  const tokenContent = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        maxWidth: 500,
      }}
    >
      {stateIcon}
      Parent:{' '}
      <Link
        aria-label={`Parent issue: ${parent.title}`}
        inline
        muted
        hoverColor="fg.muted"
        href={parent.url}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        data-hovercard-url={issueHovercardPath({
          owner,
          repo,
          issueNumber: parent.number,
        })}
        sx={{
          '&:not(:hover)': {textDecoration: 'none !important'},
          marginLeft: 1,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {small ? `#${parent.number}` : parent.title}
      </Link>
    </Box>
  )

  if (small) return tokenContent

  return (
    <>
      <Box sx={{width: 1, height: 18, borderRight: '1px solid', borderColor: 'border.default'}} />
      <div>
        <Token
          text={tokenContent}
          size={'xlarge'}
          sx={{
            px: 'var(--control-small-paddingInline-normal)',
            backgroundColor: 'canvas.default',
            fontWeight: '400',
            fontSize: 0,
            gap: 1,
          }}
        />
      </div>
    </>
  )
}

try{ HeaderParentTitle.displayName ||= 'HeaderParentTitle' } catch {}